/**
 * Builds the Excel/Word/PDF export for the Loans register.
 *
 * The on-screen table is the source of truth — not `summarizationTasks.output`,
 * which is a counts-and-narrative dump. The export is the unfiltered register:
 * one row per active loan, matching the table columns (no notes preview, no
 * confidence).
 *
 * Money is written as a real number and dates as real Date cells so the
 * practitioner can sort / sum in Excel.
 */

import {
  deriveLoanBalanceEstimates,
  resolveRocapValuationIndex,
} from "@cvx/jobs/summarization/lib/rocapValuation";
import type { ExportContent, ExportSheet } from "@/utils/export";
import type { LoanEntryUI } from "./types";

const DIRECTION_LABELS: Record<string, string> = {
  Loan_Granted: "Granted",
  Loan_Taken: "Taken",
};

const AGREEMENT_LABELS: Record<string, string> = {
  Formal_Contract: "Formal",
  Invoice_Only: "Invoice",
  No_Documentation: "No docs",
};

const SECURITY_LABELS: Record<string, string> = {
  Unsecured: "Unsecured",
  PPSR_Registered: "PPSR",
  Mortgage_Registered: "Mortgage",
};

function asDate(raw?: string): Date | "" {
  if (!raw) return "";
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : parsed;
}

/** The director's ROCAP figure for a loan (blank cell when none). */
function rocapFigure(entry: LoanEntryUI): number | "" {
  const estimates = deriveLoanBalanceEstimates(entry);
  const index = resolveRocapValuationIndex(estimates, undefined);
  return index != null ? (estimates[index]?.value ?? "") : "";
}

function loansSheet(
  entries: LoanEntryUI[],
  disclosureShort: string,
): ExportSheet {
  const sorted = [...entries]
    .filter((e) => e.status === "active")
    .sort((a, b) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0;
      const bTime = b.date ? new Date(b.date).getTime() : 0;
      const aSafe = Number.isNaN(aTime) ? 0 : aTime;
      const bSafe = Number.isNaN(bTime) ? 0 : bTime;
      if (aSafe !== bSafe) return aSafe - bSafe;
      return a.counterpartyName.localeCompare(b.counterpartyName);
    });

  return {
    name: "Loans",
    headers: [
      "Date",
      "Counterparty",
      "Total Amount",
      "Outstanding",
      disclosureShort,
      "Direction",
      "Agreement",
      "Security",
    ],
    rows: sorted.map((entry) => [
      asDate(entry.date),
      entry.counterpartyName,
      entry.amount,
      entry.outstandingBalance,
      rocapFigure(entry),
      DIRECTION_LABELS[entry.direction] ?? entry.direction,
      AGREEMENT_LABELS[entry.agreementStatus] ?? entry.agreementStatus,
      SECURITY_LABELS[entry.securityStatus] ?? entry.securityStatus,
    ]),
  };
}

export function buildLoansExport(
  entries: LoanEntryUI[],
  /**
   * Director-disclosure column header for the job's appointment type
   * (defaults to "ROCAP" for corporate insolvencies).
   */
  disclosureShort = "ROCAP",
): ExportContent {
  return {
    title: "Loans",
    sheets: [loansSheet(entries, disclosureShort)],
  };
}
